import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import WebSocket from '../../../WebSocket';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import AddVendorModal from '../../VendorsPage/AddVendorModal';
import {vendorsActions} from '../../../../state/actions/vendors.actions';
import {
  backendWebsocketActionsConstants,
  SCAActionsConstants,
} from '../../../../constants';
import {
  objectHelpers,
  scaHelpers,
  systemHelpers,
} from '../../../../utils/helpers';
import {firebaseEvents} from '../../../../snippets/firebase';

const {VENDOR_CREATION_ACTION} = SCAActionsConstants;

const VendorEditor = ({
  mode = 'add',
  open,
  onCancel,
  onSuccess,
  size = 'full',
  vendorId,

  createVendor,
  getBeneficiary,
  getVendor,
  updateVendor
}) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [addVendorModalProps, setAddVendorModalProps] = useState({error: null, loading: false});
  const [tempData, setTempData] = useState({
    data: undefined,
    id: undefined,
    errorCallbackFunction: undefined,
    successCallbackFunction: undefined,
  });
  const [isSocketOpen, setIsSocketOpen] = useState(false);
  const [vendor, setVendor] = useState(null);

  const authModalProps = useMemo(() => {
    const isVendorCreation = activeAuthAction === VENDOR_CREATION_ACTION;
    const isEditMode = mode === 'edit';

    let title = '';

    if (isVendorCreation) {
      title = isEditMode
        ? `${t('edit')} ${t('vendor')}`
        : `${t('add')} ${t('new')} ${t('vendor')}`;
    }

    return { title };
  }, [activeAuthAction, mode, t]);

  useEffect(() => {
    if (!open) {
      setAddVendorModalProps({error: null, loading: false});
      if (isSocketOpen) setIsSocketOpen(false);
    }
    if (open && !isSocketOpen) {
      setIsSocketOpen(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!vendorId || !open || vendorId === vendor?.vendor_id) return;

    getVendor({
      id: vendorId,
      successCallback: (vendor) => {
        const beneficiaryId = vendor?.beneficiary_id;
        setVendor(vendor);

        if (beneficiaryId) {
          getBeneficiary({
            id: beneficiaryId,
            successCallback: (beneficiary) => {
              setVendor({
                ...vendor,
                bic: beneficiary.bic,
                iban: beneficiary.clear_iban
              });
            }
          })
        }
      }
    });
  }, [open, vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorSCACallback = (response, errorCallback) => {
    scaHelpers.SCAResponseCallback({
      response,
      scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
      errorCallback: (response) => errorCallback && errorCallback(response)
    });
  }

  const handleOkAddVendorModal = (data, successCallback, errorCallback) => {
    handleCreateVendor({data, successCallback, errorCallback});
    setActiveAuthAction(VENDOR_CREATION_ACTION);
    setTempData({
      ...tempData,
      data,
      errorCallbackFunction: errorCallback,
      successCallbackFunction: successCallback
    });
  }

  const handleCreateVendor = ({ data, successCallback, errorCallback } = {}) => {
    const isEditMode = mode === 'edit';
    const vendorId = vendor?.vendor_id;

    if (isEditMode && !vendorId) return;

    setAddVendorModalProps({...addVendorModalProps, loading: true});

    const handleError = (response) => {
      errorSCACallback(response, () => {
        errorCallback?.(response);
        setAddVendorModalProps({...addVendorModalProps, loading: false});
      });
    };

    const handleSuccess = (response) => {
      const logEventName = isEditMode ? firebaseEvents.VENDORS_UPDATE : firebaseEvents.VENDORS_ADD_NEW;
      systemHelpers.logEvent(logEventName);
      successCallback?.(response);

      if (data.iban && data.bic) {
        return;
      }

      setAddVendorModalProps({...addVendorModalProps, error: null, loading: false});
      onSuccess?.(response);
    };

    const commonPayload = {
      data,
      headers: scaHelpers.getAuthHeaders(VENDOR_CREATION_ACTION),
      successCallback: handleSuccess,
      errorCallback: handleError
    };

    if (isEditMode) {
      updateVendor({ id: vendorId, ...commonPayload });
    } else {
      createVendor(commonPayload);
    }
  };

  const handleCancelAuthModal = () => {
    const operationActions = {
      [VENDOR_CREATION_ACTION]: () => setAddVendorModalProps({...addVendorModalProps, loading: false, error: null}),
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.(tempData);
  }

  const handleOnSuccessAuthModal = () => {
    const operationActions = {
      [VENDOR_CREATION_ACTION]: handleCreateVendor,
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.(tempData);
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    switch (action) {
      case backendWebsocketActionsConstants.VENDOR_CREATE_COMPLETED: {
        onSuccess?.();
        break;
      }
      case backendWebsocketActionsConstants.VENDOR_CREATE_ERROR: {
        let errorText = data?.beneficiary_error;
        const match = errorText.match(/[^.?!]+[.?!]/);
        if (match) {
          errorText = match[0].trim();
        }

        setAddVendorModalProps({
          ...addVendorModalProps,
          error: errorText,
          loading: false
        });
        break;
      }
      default:
        console.warn(`Unhandled websocket action: ${action}`);
    }
  };

  return (
    <>
      <AddVendorModal
        {...addVendorModalProps}
        mode={mode}
        open={open}
        onCancel={onCancel}
        onOk={handleOkAddVendorModal}
        size={size}
        vendor={vendor}
      />

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={authModalProps}
        handleCancel={handleCancelAuthModal}
        onSuccess={handleOnSuccessAuthModal}
        operationName={activeAuthAction}
      />

      {isSocketOpen && <WebSocket onMessage={handleWebsocketMessage} />}
    </>
  );
}

VendorEditor.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  size: PropTypes.oneOf(['banking', 'full', 'short']),
  vendorId: PropTypes.string,
}

const mapDispatchToProps = {
  createVendor: vendorsActions.createVendor,
  getBeneficiary: vendorsActions.getBeneficiary,
  getVendor: vendorsActions.getVendor,
  updateVendor: vendorsActions.updateVendor,
}

export default connect(null, mapDispatchToProps)(VendorEditor);
